import { useColorScheme } from '@/contexts/ColorSchemeContextProvider';
import { Menu } from 'antd';
import {
  PropsWithChildren,
  useEffect,
  useRef,
  Children,
  useState,
} from 'react';

import './styles.less';
import { useWindowSize } from '@/hooks';
import React from 'react';
import { v4 } from 'uuid';

interface Props {
  activeKey?: string;
  onChange?: (key: string) => void;
  disabled?: boolean;
}

type TabsCustomElement = React.FC<PropsWithChildren<Props>> & {
  Item: typeof TabItem;
};

const TabsCustom: TabsCustomElement = (props) => {
  const tabId = `tab-${v4()}`;
  const [activeKey, setActiveKey] = useState<string>();
  const [colorScheme] = useColorScheme();
  const { width } = useWindowSize();
  const [menuItems, setMenuItems] = useState<TabsCustomItem[]>([]);

  const tabContentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setActiveKey(props.activeKey || menuItems[0]?.key);
  }, [props.activeKey, menuItems]);

  useEffect(() => {
    setMenuItems(
      Children.map(
        props.children as React.ReactElement<TabsCustomItem>[],
        (child) => {
          return {
            key: child?.key,
            active: activeKey === child?.key,
            label: child?.props?.label,
            icon: child?.props?.icon,
            disabled: child?.props?.disabled,
          };
        },
      ).filter((item) => !!item.key) as TabsCustomItem[],
    );
  }, [props.children]);

  useEffect(() => {
    scrollTabItem(activeKey || menuItems[0]?.key);
  }, [activeKey, width]);

  useEffect(() => {
    const tabItem = document.querySelector(
      `#${tabId} .tabsCustom-item[data-active="true"] div`,
    );

    const resizeObserver = new ResizeObserver((entries) => {
      const tabItemHeight = entries[0].target.clientHeight;

      if (tabContentRef.current) {
        tabContentRef.current.style.height = `${tabItemHeight + 24}px`;
        tabContentRef.current.style.overflow = 'hidden';
      }
    });

    if (tabItem) {
      resizeObserver.observe(tabItem);
    }

    return () => {
      if (tabItem) resizeObserver.unobserve(tabItem);
    };
  }, [activeKey]);

  function scrollTabItem(key: string) {
    const index = menuItems.findIndex((item) => item.key === key);
    const rect = tabContentRef.current?.getBoundingClientRect();

    if (rect) {
      tabContentRef.current?.scrollTo({
        left: rect.width * index,
      });
    }
  }

  return (
    <div className="tabsCustom-wrapper" id={tabId}>
      <Menu
        items={menuItems}
        mode="horizontal"
        activeKey={activeKey}
        onClick={(info) => {
          setActiveKey(info.key);
          !!props.onChange && props.onChange(info.key);
        }}
        className={`tabsCustom-menu ${colorScheme}`}
      />

      <div className="tabsCustom-content" ref={tabContentRef}>
        {Children.map(
          props.children as React.ReactElement<TabsCustomItem>[],
          (child) => {
            return child
              ? React.cloneElement(child as React.ReactElement, {
                  active: child?.key === activeKey,
                  id: child?.key,
                })
              : null;
          },
        )
          .flat()
          .filter((child) => !!child?.key)}
      </div>
    </div>
  );
};

interface TabsCustomItem {
  key: string;
  label: string;
  icon?: React.ReactNode;
  disabled?: boolean;
}

const TabItem = React.forwardRef<
  HTMLDivElement,
  PropsWithChildren<TabsCustomItem & { active?: boolean; id?: string }>
>((props, ref) => {
  return (
    <div
      ref={ref}
      key={props.key}
      className="tabsCustom-item"
      id={`tab-item-${props.id}`}
      data-active={!!props.active}
    >
      <div>{props.children}</div>
    </div>
  );
});

TabsCustom.Item = TabItem;

export default TabsCustom;
